import { useState } from 'react';
import type { EnglishLesson } from '../../lib/types';
import { saveProgress } from '../../lib/db';
import AiTutorButton from '../../components/AiTutorButton';

interface EnglishLessonViewProps {
  lesson: EnglishLesson;
  onBack: () => void;
  onComplete: (lessonId: string) => void;
}

export default function EnglishLessonView({ lesson, onBack, onComplete }: EnglishLessonViewProps) {
  const { exercise } = lesson;

  const [selected, setSelected] = useState<string | null>(null);
  const [fillAnswer, setFillAnswer] = useState('');
  const [writingText, setWritingText] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [isCorrect, setIsCorrect] = useState<boolean | null>(null);

  function handleSubmit() {
    setSubmitted(true);

    if (exercise.type === 'multiple-choice') {
      setIsCorrect(selected === exercise.correctAnswer);
    } else if (exercise.type === 'fill-blank') {
      setIsCorrect(
        fillAnswer.trim().toLowerCase() === exercise.correctAnswer?.toLowerCase()
      );
    } else if (exercise.type === 'writing-prompt') {
      // Writing prompts are always accepted -- AI tutor gives feedback
      setIsCorrect(true);
    }
  }

  async function handleComplete() {
    await saveProgress({
      lessonId: lesson.id,
      moduleId: 'english',
      completedAt: new Date().toISOString(),
      score: isCorrect ? 100 : 0,
      details: {
        exerciseType: exercise.type,
        answer:
          exercise.type === 'multiple-choice'
            ? selected
            : exercise.type === 'fill-blank'
              ? fillAnswer
              : writingText,
      },
    });
    onComplete(lesson.id);
  }

  function canSubmit(): boolean {
    if (submitted) return false;
    if (exercise.type === 'multiple-choice') return selected !== null;
    if (exercise.type === 'fill-blank') return fillAnswer.trim().length > 0;
    if (exercise.type === 'writing-prompt') return writingText.trim().length > 0;
    return false;
  }

  return (
    <div className="page english-lesson-view">
      {/* Header */}
      <div className="lesson-view-header">
        <button className="btn-secondary lesson-back-btn" onClick={onBack}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          بازگشت
        </button>
        <span className="lesson-type-badge" data-type={lesson.type}>
          {lessonTypeLabel(lesson.type)}
        </span>
      </div>

      <h2 className="lesson-view-title">{lesson.titleFa}</h2>
      <p className="lesson-view-subtitle ltr">{lesson.title}</p>

      {/* Persian instruction */}
      <div className="card lesson-instruction">
        <p>{lesson.instruction}</p>
      </div>

      {/* English content in LTR area */}
      <div className="card lesson-content-card ltr">
        {lesson.content.split('\n').map((line, i) => (
          <p key={i} className="lesson-content-line">
            {line}
          </p>
        ))}
      </div>

      {/* Exercise area */}
      <div className="card lesson-exercise">
        <h3 className="exercise-title">تمرین</h3>
        <p className="exercise-prompt">{exercise.prompt}</p>

        {exercise.type === 'multiple-choice' && exercise.options && (
          <div className="exercise-options">
            {exercise.options.map((opt) => {
              let className = 'quiz-option';
              if (submitted && opt === exercise.correctAnswer) {
                className += ' correct';
              } else if (submitted && opt === selected && opt !== exercise.correctAnswer) {
                className += ' wrong';
              } else if (!submitted && opt === selected) {
                className += ' selected';
              }
              return (
                <button
                  key={opt}
                  className={className}
                  onClick={() => !submitted && setSelected(opt)}
                  disabled={submitted}
                >
                  {opt}
                </button>
              );
            })}
          </div>
        )}

        {exercise.type === 'fill-blank' && (
          <div className="exercise-fill ltr">
            <input
              type="text"
              className="fill-input"
              placeholder="پاسخ خود را بنویسید..."
              value={fillAnswer}
              onChange={(e) => setFillAnswer(e.target.value)}
              disabled={submitted}
              autoComplete="off"
              dir="ltr"
            />
          </div>
        )}

        {exercise.type === 'writing-prompt' && (
          <div className="exercise-writing">
            {exercise.hints && (
              <div className="writing-hints ltr">
                <p className="hints-label">راهنما:</p>
                <ul>
                  {exercise.hints.map((hint, i) => (
                    <li key={i}>{hint}</li>
                  ))}
                </ul>
              </div>
            )}
            <textarea
              className="writing-textarea ltr"
              rows={5}
              placeholder="Write your answer here..."
              value={writingText}
              onChange={(e) => setWritingText(e.target.value)}
              disabled={submitted}
              dir="ltr"
            />
          </div>
        )}

        {/* Submit */}
        {!submitted && (
          <button
            className="btn-primary exercise-submit-btn"
            onClick={handleSubmit}
            disabled={!canSubmit()}
          >
            بررسی پاسخ
          </button>
        )}

        {/* Feedback */}
        {submitted && (
          <div className={`exercise-feedback ${isCorrect ? 'feedback-correct' : 'feedback-wrong'}`}>
            {isCorrect ? (
              <>
                <span className="feedback-icon">{'\u2705'}</span>
                <span>آفرین! پاسخ درست است.</span>
              </>
            ) : (
              <>
                <span className="feedback-icon">{'\u274C'}</span>
                <span>
                  پاسخ درست: <strong className="ltr">{exercise.correctAnswer}</strong>
                </span>
              </>
            )}
          </div>
        )}
      </div>

      {/* AI Tutor */}
      <div className="lesson-ai-tutor">
        <AiTutorButton
          module="english"
          context={`Lesson: ${lesson.title} (${lesson.titleFa}). Content: ${lesson.content}. Exercise: ${exercise.prompt}. ${writingText ? `User wrote: ${writingText}` : ''}`}
          buttonText="کمک می‌خواهم"
        />
      </div>

      {/* Bottom actions */}
      {submitted && (
        <div className="lesson-view-actions">
          <button className="btn-primary lesson-next-btn" onClick={handleComplete}>
            درس بعدی
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
          <button className="btn-secondary" onClick={onBack}>
            بازگشت
          </button>
        </div>
      )}
    </div>
  );
}

function lessonTypeLabel(type: EnglishLesson['type']): string {
  switch (type) {
    case 'vocab':
      return 'واژگان';
    case 'grammar':
      return 'گرامر';
    case 'reading':
      return 'خواندن';
    case 'writing':
      return 'نوشتن';
  }
}
